/**
 * SparkTrend — lazy per-brand BE sparkline for portfolio cards (SYR-85).
 *
 * Fetches the rolling-bucket trend on mount (cached server-side) and renders
 * a minimal single-series line. Buckets below the minimum response threshold
 * are gapped, matching the Overview chart conventions.
 */

import React, { useMemo } from 'react';
import {
    Line,
    LineChart,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useIntl } from 'react-intl';
import { useAuth } from '../../hooks/useAuth';
import { useGetAgencyBrandTrendQuery } from '../../services/nestjs-base-api';
import { THEME } from '../../components/MarketerView/config/theme';

const LINE_COLOUR = '#3d5a80';

interface SparkTrendProps {
    brandId: number;
    period: string;
    height?: number;
}

export const SparkTrend: React.FunctionComponent<SparkTrendProps> = ({
    brandId,
    period,
    height = 56,
}) => {
    const intl = useIntl();
    const { user } = useAuth();
    const { data, isLoading } = useGetAgencyBrandTrendQuery({
        jwtToken: user?.jwtToken || '',
        brandId,
        period,
    });

    const points = useMemo(() => {
        if (!data) return [];
        return data.timeSeriesData.map((p) => ({
            month: p.month,
            score:
                p.totalResponses >= data.minResponseThreshold
                    ? p.overallBrandEquityScore
                    : null,
            responses: p.totalResponses,
        }));
    }, [data]);

    const visible = points.filter((p) => p.score !== null);

    if (isLoading) {
        return (
            <div
                style={{
                    height,
                    borderRadius: 8,
                    background: THEME.bgChip,
                    opacity: 0.6,
                }}
            />
        );
    }

    if (visible.length < 2) {
        return (
            <div
                style={{
                    height,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    color: THEME.textMuted,
                }}
            >
                {intl.formatMessage({
                    id: 'agency.spark.noTrend',
                    defaultMessage: 'Trend appears with more data',
                })}
            </div>
        );
    }

    return (
        <div style={{ height, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height={height}>
                <LineChart data={points} margin={{ top: 6, right: 4, bottom: 2, left: 4 }}>
                    {/* Hidden axis so the tooltip label is the bucket date,
                        not the array index. */}
                    <XAxis dataKey="month" hide />
                    <YAxis hide domain={['dataMin - 5', 'dataMax + 5']} />
                    <ReferenceLine y={0} stroke={THEME.border} strokeWidth={1} />
                    <Tooltip
                        cursor={{ stroke: THEME.border, strokeWidth: 1 }}
                        contentStyle={{
                            borderRadius: 8,
                            border: `1px solid ${THEME.border}`,
                            boxShadow: THEME.cardShadow,
                            fontSize: 12,
                            padding: '6px 10px',
                        }}
                        formatter={(value: any) => [
                            `${Math.round(Number(value))}%`,
                            intl.formatMessage({
                                id: 'agency.spark.tooltipScore',
                                defaultMessage: 'Brand equity',
                            }),
                        ]}
                        labelFormatter={(label: any) => {
                            const d = new Date(label);
                            return Number.isNaN(d.getTime())
                                ? ''
                                : d.toLocaleDateString('en-GB', {
                                      day: 'numeric',
                                      month: 'short',
                                      year: 'numeric',
                                  });
                        }}
                    />
                    {/* Sub-threshold windows contribute no data point, but a
                        56px sparkline with hard gaps reads as broken — draw
                        one continuous line through the valid points. The full
                        trend chart on the scorecard keeps the explicit gaps. */}
                    <Line
                        type="monotone"
                        dataKey="score"
                        stroke={LINE_COLOUR}
                        strokeWidth={2}
                        dot={false}
                        activeDot={{ r: 4, fill: LINE_COLOUR, stroke: '#fff', strokeWidth: 2 }}
                        connectNulls
                        isAnimationActive={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};
